// bluaIQ — TASK-WEB-1: SYNTHETIC development/test fixture for the read-only Takeoff
// display module.
//
// FIXTURE_ONLY. Every value below is hand-invented for this task. It does NOT mirror,
// copy, or derive from any real benchmark project tracked in the TAKEOFF repo — that
// data must stay fixture-only inside the TAKEOFF repo itself (standing rule: benchmark
// fixture isolation). This module reaches no backend, performs no fetch, and creates
// nothing.
//
// The fixture intentionally exercises every UI-relevant contract v2 shape: all 4
// quantification statuses, a multi-source line, and one redacted-amount note (so the
// "Amount hidden" / "Monto oculto" badge path has something real to render).
//
// v4 shapes (TASK-WEB-4) — also exercised with INVENTED values (deliberately different
// from every number the real benchmark project produced): two trim lines carrying a
// point measurement PLUS a coverage band in the note, one exterior waterfall line
// (gross − exact deductions = net, doors NOT applied, material alternatives without
// prices), and five MEP guide lines in "MEP — …" sections with mep_guide_only=true.
//
// DUAL DEMO (TASK-WEB-6, contract v3): the fixture carries TWO invented houses behind
// a selector. "House Alpha" (the original ~15-line payload) stays a PURE v2 payload —
// no plan_qa_report, no contract_version — proving a v2 payload remains v3-valid.
// "House Beta" (~10 lines) is v3-DECLARED (contract_version = v3 label rule) and
// exercises plan_qa_report exactly as the promulgated generator ships it. NO data from
// either real benchmark plan set appears anywhere in this module (enforced by the
// anti-hardcode smoke tests).

import type { BilingualLabel, WebsiteTakeoffPayloadV2 } from '../../shared/websitePayloadContractV2'
import { REQUIRED_WARNING_ENUMS } from '../../shared/websitePayloadContractV2'
import type { WebsiteTakeoffPayloadV3 } from '../../shared/websitePayloadContractV3'
import { CONTRACT_VERSION_V3 } from '../../shared/websitePayloadContractV3'

export const IS_FIXTURE_ONLY = true as const

export const takeoffReadonlyDisplayFixture: WebsiteTakeoffPayloadV2 = {
  sourceDocumentHash: 'synthetic-fixture-sha256-0000000000000000000000000000000000000000000000000000000000000000',
  read_run_id: 'synthetic-fixture-read-run-0001',
  website_readiness_status: 'candidate_only_preview',
  warnings: [...REQUIRED_WARNING_ENUMS],
  labels: {
    readonly_notice: {
      en: 'Read-only view — for review, not a purchase order',
      es: 'Vista de solo lectura — para revisión, no es una orden de compra',
    },
    candidate_only: {
      en: 'Candidate quantity — confirm before buying',
      es: 'Cantidad candidata — confirmar antes de comprar',
    },
    plan_text_original_language: {
      en: 'Plan text shown in its original language',
      es: 'Texto del plano mostrado en su idioma original',
    },
  },
  safe_next_action: 'Revise las líneas con sus chips de estado. Nada aquí crea registros posteriores. / Review the lines with their status chips. Nothing here creates downstream records.',
  quantification_lines_candidate_only: [
    {
      section: 'Windows',
      item_description: 'Vinyl window, single hung, 3050',
      quantity_value: 6,
      quantity_unit: 'each',
      quantification_status: 'quantity_confirmed_from_plan',
      sources: [
        { evidence_class: 'plan_schedule', ref: 'synthetic-plan-page-04:W1-W6', source_page: '04' },
      ],
      notes: 'Confirmado desde el cuadro de ventanas del plano arquitectónico. / Confirmed from the architectural window schedule.',
      packaging_only: true,
      purchase_ready: false,
      is_purchase_quantity: false,
      is_order_price_bearing: false,
    },
    {
      section: 'Windows',
      item_description: 'Vinyl window, fixed, 2020',
      quantity_value: null,
      quantity_unit: 'each',
      quantification_status: 'spec_present_quantity_pending',
      sources: [
        { evidence_class: 'pdf_schedule', ref: 'synthetic-plan-page-04:FX1', source_page: '04' },
      ],
      notes: 'Especificación identificada en el cuadro; medición pendiente. / Specification identified on the schedule; measurement pending.',
      packaging_only: true,
      purchase_ready: false,
      is_purchase_quantity: false,
      is_order_price_bearing: false,
    },
    {
      section: 'Insulation',
      item_description: 'R-13 fiberglass batt insulation, exterior walls',
      quantity_value: 1850,
      quantity_unit: 'sf',
      quantification_status: 'quantity_estimated_from_user_budget',
      sources: [
        {
          evidence_class: 'user_budget',
          ref: 'synthetic-owner-budget:line-04',
          trust_tier: 'user_provided',
          designation: 'owner_modification_vs_plan',
        },
      ],
      notes:
        'Estimado según la línea del presupuesto enviado por la propietaria — la línea original decía '
        + '"R-13 batt insulation [amount not shown in web view / monto no mostrado en la vista web] '
        + 'confirm before ordering." / Estimated from the owner’s submitted budget line — original budget line read '
        + '"R-13 batt insulation [amount not shown in web view / monto no mostrado en la vista web] '
        + 'confirm before ordering."',
      packaging_only: true,
      purchase_ready: false,
      is_purchase_quantity: false,
      is_order_price_bearing: false,
    },
    {
      section: 'Insulation',
      item_description: 'R-19 fiberglass batt insulation, ceiling',
      quantity_value: null,
      quantity_unit: 'sf',
      quantification_status: 'quantity_reading_in_progress',
      sources: [
        {
          evidence_class: 'pdf_reading_in_progress',
          ref: 'synthetic-plan-page-06:R-19',
          source_page: '06',
          classification: 'needs_additional_pass',
        },
      ],
      notes: 'Lectura automática adicional en proceso para la anotación de aislamiento del techo. / Additional automated reading in progress for the ceiling insulation callout.',
      packaging_only: true,
      purchase_ready: false,
      is_purchase_quantity: false,
      is_order_price_bearing: false,
    },
    {
      section: 'Drywall',
      item_description: '1/2 in. drywall, interior walls',
      quantity_value: 4200,
      quantity_unit: 'sf',
      quantification_status: 'quantity_confirmed_from_plan',
      sources: [
        { evidence_class: 'plan_schedule', ref: 'synthetic-plan-page-07:DRYWALL', source_page: '07' },
      ],
      notes: 'Confirmado desde el cuadro de acabados interiores. / Confirmed from the interior finish schedule.',
      packaging_only: true,
      purchase_ready: false,
      is_purchase_quantity: false,
      is_order_price_bearing: false,
    },
    {
      section: 'Drywall',
      item_description: '5/8 in. Type X drywall, garage separation wall',
      quantity_value: null,
      quantity_unit: 'sf',
      quantification_status: 'spec_present_quantity_pending',
      sources: [
        { evidence_class: 'pdf_schedule', ref: 'synthetic-plan-page-07:TYPE-X', source_page: '07' },
      ],
      notes: 'Especificación identificada para el ensamble resistente al fuego; medición pendiente. / Specification identified for the fire-rated assembly; measurement pending.',
      packaging_only: true,
      purchase_ready: false,
      is_purchase_quantity: false,
      is_order_price_bearing: false,
    },
    {
      section: 'Drywall',
      item_description: 'Drywall corner bead, standard',
      quantity_value: 320,
      quantity_unit: 'lf',
      quantification_status: 'quantity_estimated_from_user_budget',
      sources: [
        {
          evidence_class: 'user_budget',
          ref: 'synthetic-owner-budget:line-09',
          trust_tier: 'user_provided',
          designation: 'owner_modification_vs_plan',
        },
      ],
      notes: 'Estimado según la línea del presupuesto enviado por la propietaria. / Estimated from the owner’s submitted budget line.',
      packaging_only: true,
      purchase_ready: false,
      is_purchase_quantity: false,
      is_order_price_bearing: false,
    },
    // ── v4: point measurement + coverage band carried in the note ──────────────
    {
      section: 'Interior trim',
      item_description: 'Baseboard, MDF 3-1/4 in. (rodapié)',
      quantity_value: 512,
      quantity_unit: 'lf',
      quantification_status: 'quantity_confirmed_from_plan',
      sources: [
        { evidence_class: 'plan_room_labels', ref: 'synthetic-plan-page-02:ROOM-LABELS', source_page: '02' },
      ],
      notes:
        'Medición puntual solo de cuartos etiquetados; la banda residual de cobertura aparece en los '
        + 'chips y cubre clósets y pasillos sin etiqueta. / '
        + 'Point measured from labeled rooms only. Residual coverage band: 585.0–730.0 lf / '
        + '54–68 pzas — covers unlabeled closets and hallways; the plan gives no '
        + 'machine-readable shape for them.',
      packaging_only: true,
      purchase_ready: false,
      is_purchase_quantity: false,
      is_order_price_bearing: false,
    },
    {
      section: 'Interior trim',
      item_description: 'Quarter round, pine (cuarto bocel)',
      quantity_value: 498,
      quantity_unit: 'lf',
      quantification_status: 'quantity_confirmed_from_plan',
      sources: [
        { evidence_class: 'plan_room_labels', ref: 'synthetic-plan-page-02:ROOM-LABELS', source_page: '02' },
      ],
      notes:
        'Medición puntual solo de cuartos etiquetados; banda residual en los chips — mismo modelo '
        + 'residual que la línea de rodapié. / '
        + 'Point measured from labeled rooms only. Residual coverage band: 560.0–702.0 lf / '
        + '51–64 pzas — same residual model as the baseboard line.',
      packaging_only: true,
      purchase_ready: false,
      is_purchase_quantity: false,
      is_order_price_bearing: false,
    },
    // ── v4: exterior waterfall line (gross − exact deductions = net) ────────────
    {
      section: 'Exterior / Envolvente',
      item_description: 'Exterior wall cladding, net area (material selection pending)',
      quantity_value: 1750,
      quantity_unit: 'sf',
      quantification_status: 'quantity_confirmed_from_plan',
      sources: [
        { evidence_class: 'plan_elevations', ref: 'synthetic-plan-page-03:ELEVATIONS', source_page: '03' },
      ],
      notes:
        'Cascada: bruto − deducciones exactas = neto (ver la tarjeta). Puertas NO aplicadas. '
        + 'Selección de material pendiente. Alternativas sin precios en la tarjeta. / '
        + 'Waterfall: gross 1980 sf − exact window openings 230 sf = net 1750 sf. '
        + 'Doors NOT applied. Material selection pending. Alternatives (no prices): fiber cement lap '
        + 'siding | engineered wood panel siding | three-coat stucco.',
      packaging_only: true,
      purchase_ready: false,
      is_purchase_quantity: false,
      is_order_price_bearing: false,
    },
    // ── v4: MEP guide/proposal lines — NEVER material quantities ────────────────
    {
      section: 'MEP — Guías y propuestas / Eléctrico',
      item_description: 'Receptacle points, code-style layout proposal',
      quantity_value: 34,
      quantity_unit: 'each',
      quantification_status: 'spec_present_quantity_pending',
      sources: [
        { evidence_class: 'rule_engine_proposal', ref: 'synthetic-mep-electrical-v1:receptacles' },
      ],
      notes:
        'Guía preliminar generada por reglas — requiere revisión local/profesional; solo propuesta, '
        + 'no es cumplimiento normativo. / Rule-generated preliminary guide — requires local/professional '
        + 'review. Proposal only, not code compliance.',
      packaging_only: true,
      purchase_ready: false,
      is_purchase_quantity: false,
      is_order_price_bearing: false,
      mep_guide_only: true,
    },
    {
      section: 'MEP — Guías y propuestas / Eléctrico',
      item_description: 'Smoke detector points, layout proposal',
      quantity_value: 4,
      quantity_unit: 'each',
      quantification_status: 'spec_present_quantity_pending',
      sources: [
        { evidence_class: 'rule_engine_proposal', ref: 'synthetic-mep-electrical-v1:smoke-detectors' },
      ],
      notes:
        'Guía preliminar generada por reglas — requiere revisión local/profesional; solo propuesta, '
        + 'no es cumplimiento normativo. / Rule-generated preliminary guide — requires local/professional '
        + 'review. Proposal only, not code compliance.',
      packaging_only: true,
      purchase_ready: false,
      is_purchase_quantity: false,
      is_order_price_bearing: false,
      mep_guide_only: true,
    },
    {
      section: 'MEP — Guías y propuestas / Plomería',
      item_description: 'Plumbing fixture count, group assignment proposal',
      quantity_value: 6,
      quantity_unit: 'each',
      quantification_status: 'spec_present_quantity_pending',
      sources: [
        { evidence_class: 'rule_engine_proposal', ref: 'synthetic-mep-plumbing-v1:fixtures' },
      ],
      notes:
        'Asignación por grupo — no es longitud final; guía preliminar — requiere revisión '
        + 'local/profesional. / Group assignment — not a final length; preliminary guide — requires '
        + 'local/professional review.',
      packaging_only: true,
      purchase_ready: false,
      is_purchase_quantity: false,
      is_order_price_bearing: false,
      mep_guide_only: true,
    },
    {
      section: 'MEP — Guías y propuestas / Plomería',
      item_description: 'Supply piping, group assignment proposal',
      quantity_value: null,
      quantity_unit: 'lf',
      quantification_status: 'quantity_reading_in_progress',
      sources: [
        { evidence_class: 'rule_engine_proposal', ref: 'synthetic-mep-plumbing-v1:piping-groups' },
      ],
      notes:
        'Asignación por grupo — no es longitud final; guía preliminar — requiere revisión '
        + 'local/profesional. / Group assignment — not a final length; preliminary guide — requires '
        + 'local/professional review.',
      packaging_only: true,
      purchase_ready: false,
      is_purchase_quantity: false,
      is_order_price_bearing: false,
      mep_guide_only: true,
    },
    {
      section: 'MEP — Guías y propuestas / HVAC',
      item_description: 'Cooling capacity, Manual J inputs proposal',
      quantity_value: null,
      quantity_unit: 'ton',
      quantification_status: 'spec_present_quantity_pending',
      sources: [
        { evidence_class: 'rule_engine_proposal', ref: 'synthetic-mep-hvac-v1:manual-j-inputs' },
      ],
      notes:
        'Solo propuesta de entradas Manual J — 2.0 a 3.0 ton indicativo, no es dimensionamiento '
        + 'final de HVAC; requiere un ingeniero para cerrar el cálculo. / Manual J inputs proposal '
        + 'only — 2.0 to 3.0 ton indicative, not final HVAC sizing/engineering; requires an engineer '
        + 'to close the calculation.',
      packaging_only: true,
      purchase_ready: false,
      is_purchase_quantity: false,
      is_order_price_bearing: false,
      mep_guide_only: true,
    },
  ],
}

// ── House Beta — second invented payload, v3-declared, exercises plan_qa_report ──
export const takeoffReadonlyDisplayFixtureBeta: WebsiteTakeoffPayloadV3 = {
  sourceDocumentHash: 'synthetic-fixture-beta-sha256-1111111111111111111111111111111111111111111111111111111111111111',
  read_run_id: 'synthetic-fixture-read-run-0002',
  // v3 label rule: a payload carrying plan_qa_report MUST declare itself v3.
  contract_version: CONTRACT_VERSION_V3,
  website_readiness_status: 'candidate_only_preview',
  warnings: [...REQUIRED_WARNING_ENUMS],
  labels: {
    readonly_notice: {
      en: 'Read-only view — for review, not a purchase order',
      es: 'Vista de solo lectura — para revisión, no es una orden de compra',
    },
    candidate_only: {
      en: 'Candidate quantity — confirm before buying',
      es: 'Cantidad candidata — confirmar antes de comprar',
    },
    plan_text_original_language: {
      en: 'Plan text shown in its original language',
      es: 'Texto del plano mostrado en su idioma original',
    },
  },
  safe_next_action: 'Revise primero los hallazgos de la revisión del plano y luego las líneas candidatas. Nada aquí crea registros posteriores. / Review the plan review findings first, then the candidate lines. Nothing here creates downstream records.',
  plan_qa_report: {
    headline: {
      en: 'Plan inconsistencies found and corrected — added certainty for your quantities',
      es: 'Inconsistencias del plano encontradas y corregidas — más certeza para sus cantidades',
    },
    // 5 synthetic issues covering the v3 status enums (label_absent_in_rev2 is
    // exercised by the validator smoke tests) — including one issue whose
    // before/after are STRUCTURED DIMENSION RECORDS, copying the shape the real
    // generator ships ({width_ft, depth_ft, area_sf, was_suspect}) with invented
    // values (TASK-WEB-7; anti-hardcode tests still apply).
    issues: [
      {
        issue: 'Duplicated room label in the laundry area / Etiqueta de cuarto duplicada en el área de lavandería',
        status: 'resolved_by_plan_revision',
        before: 'Dos cuartos etiquetados LAUNDRY en la página 2 de la revisión 1 / Two rooms labeled LAUNDRY on page 2 of revision 1',
        after: 'Etiqueta LAUNDRY única confirmada en la revisión 2 / Single LAUNDRY label confirmed in revision 2',
      },
      {
        issue: 'Room 205 use label missing / Falta la etiqueta de uso del cuarto 205',
        status: 'resolved_by_user_designation',
        before: 'El cuarto 205 no traía etiqueta de uso en las revisiones 1 y 2 / Room 205 carried no use label in revisions 1 and 2',
        after: 'La propietaria designó el cuarto 205 como PANTRY — cantidades actualizadas con esa designación / Owner designated room 205 as PANTRY — quantities updated with that designation',
      },
      {
        issue: 'Hallway width label conflicts with the dimension chain / La etiqueta de ancho del pasillo contradice la cadena de dimensiones',
        status: 'label_error_confirmed_resolution_pending_corrected_dimension',
        before: 'El pasillo está etiquetado con 3 pies en la página 2; la cadena de dimensiones cierra en 4 pies / Hallway labeled 3 ft on page 2; the dimension chain closes at 4 ft',
        after: 'Error de etiqueta confirmado — dimensión corregida pendiente en la próxima revisión del plano / Label error confirmed — corrected dimension pending in the next plan revision',
      },
      {
        issue: 'Storage room dimensions were suspect in revision 1 / Las dimensiones del cuarto de almacenamiento eran sospechosas en la revisión 1',
        status: 'resolved_by_plan_revision',
        // Structured dimension records — the real generator's before/after shape.
        before: { width_ft: 3.1, depth_ft: 21.4, area_sf: 66.3, was_suspect: true },
        after: { width_ft: 4.1, depth_ft: 21.4, area_sf: 87.7, was_suspect: false },
      },
      {
        issue: 'Window tag count mismatch on the north elevation / Discrepancia de etiquetas de ventana en la elevación norte',
        status: 'pending',
        before: 'La elevación muestra 4 etiquetas; el cuadro lista 3 / Elevation shows 4 tags; the schedule lists 3',
        after: 'En espera de confirmación de la propietaria / Awaiting owner confirmation',
      },
    ],
    designations_applied: [
      'owner_designation: laundry label consolidation confirmed',
      'owner_designation: room 205 designated as PANTRY',
      'owner_designation: window tag recount requested',
    ],
  },
  quantification_lines_candidate_only: [
    {
      section: 'Windows',
      item_description: 'Aluminum window, sliding, 4040',
      quantity_value: 3,
      quantity_unit: 'each',
      quantification_status: 'quantity_confirmed_from_plan',
      sources: [
        { evidence_class: 'plan_schedule', ref: 'synthetic-beta-plan-page-03:WS1-WS3', source_page: '03' },
      ],
      notes: 'Confirmado desde el cuadro de ventanas de la segunda casa de prueba. / Confirmed from the window schedule of the second test house.',
      packaging_only: true,
      purchase_ready: false,
      is_purchase_quantity: false,
      is_order_price_bearing: false,
    },
    {
      section: 'Windows',
      item_description: 'Aluminum window, fixed, 3030',
      quantity_value: null,
      quantity_unit: 'each',
      quantification_status: 'spec_present_quantity_pending',
      sources: [
        { evidence_class: 'pdf_schedule', ref: 'synthetic-beta-plan-page-03:FXB', source_page: '03' },
      ],
      notes: 'Especificación identificada; medición pendiente. / Specification identified; measurement pending.',
      packaging_only: true,
      purchase_ready: false,
      is_purchase_quantity: false,
      is_order_price_bearing: false,
    },
    {
      section: 'Doors',
      item_description: 'Interior door, hollow core, 2868',
      quantity_value: 8,
      quantity_unit: 'each',
      quantification_status: 'quantity_confirmed_from_plan',
      sources: [
        { evidence_class: 'plan_schedule', ref: 'synthetic-beta-plan-page-04:D-SCHED', source_page: '04' },
      ],
      notes: 'Confirmado desde el cuadro de puertas. / Confirmed from the door schedule.',
      packaging_only: true,
      purchase_ready: false,
      is_purchase_quantity: false,
      is_order_price_bearing: false,
    },
    {
      section: 'Drywall',
      item_description: '1/2 in. drywall, interior walls',
      quantity_value: 2600,
      quantity_unit: 'sf',
      quantification_status: 'quantity_confirmed_from_plan',
      sources: [
        { evidence_class: 'plan_schedule', ref: 'synthetic-beta-plan-page-05:DRYWALL', source_page: '05' },
      ],
      notes: 'Confirmado desde el cuadro de acabados interiores. / Confirmed from the interior finish schedule.',
      packaging_only: true,
      purchase_ready: false,
      is_purchase_quantity: false,
      is_order_price_bearing: false,
    },
    {
      section: 'Drywall',
      item_description: 'Drywall corner bead, standard',
      quantity_value: 240,
      quantity_unit: 'lf',
      quantification_status: 'quantity_estimated_from_user_budget',
      sources: [
        {
          evidence_class: 'user_budget',
          ref: 'synthetic-beta-owner-budget:line-06',
          trust_tier: 'user_provided',
          designation: 'owner_modification_vs_plan',
        },
      ],
      notes:
        'Estimado según la línea del presupuesto enviado por la propietaria — la línea original decía '
        + '"corner bead [amount not shown in web view / monto no mostrado en la vista web] '
        + 'confirm before ordering." / Estimated from the owner’s submitted budget line — original budget line read '
        + '"corner bead [amount not shown in web view / monto no mostrado en la vista web] '
        + 'confirm before ordering."',
      packaging_only: true,
      purchase_ready: false,
      is_purchase_quantity: false,
      is_order_price_bearing: false,
    },
    {
      section: 'Interior trim',
      item_description: 'Baseboard, pine 2-1/4 in. (rodapié)',
      quantity_value: 310,
      quantity_unit: 'lf',
      quantification_status: 'quantity_confirmed_from_plan',
      sources: [
        { evidence_class: 'plan_room_labels', ref: 'synthetic-beta-plan-page-02:ROOM-LABELS', source_page: '02' },
      ],
      notes:
        'Medición puntual solo de cuartos etiquetados; banda residual en los chips — cubre clósets '
        + 'sin etiqueta. / '
        + 'Point measured from labeled rooms only. Residual coverage band: 340.0–420.0 lf / '
        + '31–39 pzas — covers unlabeled closets.',
      packaging_only: true,
      purchase_ready: false,
      is_purchase_quantity: false,
      is_order_price_bearing: false,
    },
    {
      section: 'Exterior / Envolvente',
      item_description: 'Revestimiento exterior (siding)',
      quantity_value: 980,
      quantity_unit: 'sf',
      quantification_status: 'quantity_confirmed_from_plan',
      sources: [
        { evidence_class: 'plan_elevations', ref: 'synthetic-beta-plan-page-06:ELEVATIONS', source_page: '06' },
      ],
      notes:
        'Cantidad candidata — no lista para compra. Pendientes: gabletes pendientes y '
        + 'puertas pendientes (aún no descontados). / Candidate quantity — not ready for '
        + 'purchase. Pending: gables and doors (not yet deducted).',
      packaging_only: true,
      purchase_ready: false,
      is_purchase_quantity: false,
      is_order_price_bearing: false,
    },
    {
      section: 'Insulation',
      item_description: 'R-15 batt insulation, exterior walls',
      quantity_value: null,
      quantity_unit: 'sf',
      quantification_status: 'quantity_reading_in_progress',
      sources: [
        {
          evidence_class: 'pdf_reading_in_progress',
          ref: 'synthetic-beta-plan-page-05:R-15',
          source_page: '05',
          classification: 'needs_additional_pass',
        },
      ],
      notes: 'Lectura automática adicional en proceso para la anotación de aislamiento de muros. / Additional automated reading in progress for the wall insulation callout.',
      packaging_only: true,
      purchase_ready: false,
      is_purchase_quantity: false,
      is_order_price_bearing: false,
    },
    {
      section: 'MEP — Guías y propuestas / Eléctrico',
      item_description: 'Receptacle points, layout proposal',
      quantity_value: 22,
      quantity_unit: 'each',
      quantification_status: 'spec_present_quantity_pending',
      sources: [
        { evidence_class: 'rule_engine_proposal', ref: 'synthetic-beta-mep-electrical-v1:receptacles' },
      ],
      notes:
        'Guía preliminar generada por reglas — requiere revisión local/profesional; solo propuesta, '
        + 'no es cumplimiento normativo. / Rule-generated preliminary guide — requires local/professional '
        + 'review. Proposal only, not code compliance.',
      packaging_only: true,
      purchase_ready: false,
      is_purchase_quantity: false,
      is_order_price_bearing: false,
      mep_guide_only: true,
    },
    {
      section: 'MEP — Guías y propuestas / HVAC',
      item_description: 'Cooling capacity, Manual J inputs proposal',
      quantity_value: null,
      quantity_unit: 'ton',
      quantification_status: 'spec_present_quantity_pending',
      sources: [
        { evidence_class: 'rule_engine_proposal', ref: 'synthetic-beta-mep-hvac-v1:manual-j-inputs' },
      ],
      notes:
        'Solo propuesta de entradas Manual J — 1.5 a 2.5 ton indicativo, no es dimensionamiento '
        + 'final de HVAC; requiere un ingeniero para cerrar el cálculo. / Manual J inputs proposal '
        + 'only — 1.5 to 2.5 ton indicative, not final HVAC sizing/engineering; requires an engineer '
        + 'to close the calculation.',
      packaging_only: true,
      purchase_ready: false,
      is_purchase_quantity: false,
      is_order_price_bearing: false,
      mep_guide_only: true,
    },
  ],
}

// ── House selector model ──────────────────────────────────────────────────────
// Payloads are typed against v3 (a pure-v2 payload like Alpha is v3-assignable —
// exactly the additive relationship the promulgated contract defines).
export interface TakeoffDisplayHouse {
  id: string
  label: BilingualLabel
  payload: WebsiteTakeoffPayloadV3
}

export const takeoffReadonlyDisplayHouses: TakeoffDisplayHouse[] = [
  {
    id: 'alpha',
    label: { en: 'House Alpha (test data)', es: 'Casa Alpha (datos de prueba)' },
    payload: takeoffReadonlyDisplayFixture,
  },
  {
    id: 'beta',
    label: { en: 'House Beta (test data)', es: 'Casa Beta (datos de prueba)' },
    payload: takeoffReadonlyDisplayFixtureBeta,
  },
]
